import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccessControl } from "../context/useAccessControl";
import {
  buildOperationalOrdersStorageKey,
  getOperationalOrdersState,
  OPERATIONAL_ORDERS_UPDATED_EVENT,
  saveOperationalOrdersState,
} from "../../services/local/operationalOrders";
import {
  hydrateSharedOperationalStateToLocal,
  pushSharedOperationalState,
  pushSharedWaiterOperationsState,
  subscribeToSharedOperationalStateChanges,
} from "../../services/supabase/operationalStateService";
import {
  buildWaiterOperationsStorageKey,
  createDeliveryAlert,
  getWaiterOperationsState,
  WAITER_OPERATIONS_REMOTE_SYNC_SOURCE,
  WAITER_OPERATIONS_UPDATED_EVENT,
} from "../../services/local/waiterOperations";
import {
  buildRestaurantProfileStorageKey,
  getRestaurantProfile,
  mergeRemoteBranchIntoProfilePreservingLocalPreferences,
  RESTAURANT_PROFILE_UPDATED_EVENT,
  saveRestaurantProfile,
} from "../../services/local/restaurantProfile";
import {
  buildRestaurantBrandTheme,
} from "../../services/local/brandTheme";
import {
  applyHeadBrandingAssets,
  applyInstitutionalBrandingAssets,
} from "../../services/local/institutionalBranding";
import { getBranchPublicProfile } from "../../services/supabase/companyAdminService";
import { transitionExternalOrderStatus } from "../../services/supabase/externalOrdersService";
import { setDocumentTitle } from "../../services/local/pageTitle";
import {
  filterKitchenOrderItems,
  getOperationalOrderKitchenStage,
  shouldShowOperationalOrderOnKitchenBoard,
} from "../../services/local/orderModel";
import {
  formatKitchenRecipeQuantity,
  getKitchenRecipeIngredients,
} from "../../services/local/kitchenRecipe";

const EMPTY_STATE = {
  mesasActivas: [],
  pedidosHistoricos: [],
};
const SHARED_OPERATIONAL_SYNC_INTERVAL_MS = 4000;
const SHARED_OPERATIONAL_SYNC_TIMEOUT_MS = 7000;
const OPERATIONAL_REMOTE_SYNC_HOLD_MS = 6500;
const KITCHEN_RECIPE_HISTORY_STATE_KEY = "mozzoKitchenRecipe";

const formatElapsed = (dateValue) => {
  if (!dateValue) {
    return "recién";
  }

  const diffMs = Math.max(0, Date.now() - new Date(dateValue).getTime());
  const diffMinutes = Math.max(0, Math.round(diffMs / 60000));

  if (diffMinutes < 1) {
    return "recién";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} min`;
  }

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const getCustomerName = (order) => String(order?.customerName || "").trim();

const isKioskPrinterKitchenOrder = (order) =>
  String(order?.source || "").trim().toLowerCase() === "kiosk" &&
  String(order?.kitchenModeSnapshot || "").trim().toLowerCase() === "printer";

const getScreenKitchenStageLabel = (stage) => {
  if (stage === "new") {
    return "Pendiente de tomar";
  }

  if (stage === "preparing") {
    return "En preparacion";
  }

  if (stage === "ready") {
    return "Listo para entregar";
  }

  return "Fuera de tablero";
};

const getScreenKitchenStageDetail = (stage) => {
  if (stage === "new") {
    return "El pedido ya entro a la pantalla, pero cocina todavia no lo tomo.";
  }

  if (stage === "preparing") {
    return "La cocina ya lo tomo y sigue en preparacion.";
  }

  if (stage === "ready") {
    return "Cocina ya lo marco como listo y ahora espera entrega desde salon.";
  }

  return "Este pedido no forma parte del flujo de pantalla de cocina.";
};

const getScreenKitchenNextStep = (stage) => {
  if (stage === "new") {
    return "Tomalo en cocina para empezar la preparacion.";
  }

  if (stage === "preparing") {
    return "Cuando termines, marcalo como listo para avisar a salon.";
  }

  if (stage === "ready") {
    return "Salon ya puede asignar mozo o registrar la entrega manual.";
  }

  return "Sin acciones pendientes en este tablero.";
};

const getOrderSummary = (order) =>
  filterKitchenOrderItems(order?.items).length > 0
    ? filterKitchenOrderItems(order?.items)
        .map((item) => item.nombre || item.productName || "Producto")
        .join(", ")
    : "Sin items cargados";

const getOrderItemCount = (order) =>
  filterKitchenOrderItems(order?.items).reduce(
    (acc, item) => acc + Number(item.quantity || 1),
    0
  );

const getOrderItemSelectionDetails = (item) => {
  const details = Array.isArray(item?.selectionDetails)
    ? item.selectionDetails
    : Array.isArray(item?.selection_details)
      ? item.selection_details
      : [];

  return details.map((detail) => String(detail || "").trim()).filter(Boolean);
};

const getSelectionDetailStyle = (detail) => {
  const normalizedDetail = String(detail || "").trim().toLowerCase();

  if (normalizedDetail.startsWith("+") || normalizedDetail.startsWith("agregar ")) {
    return styles.kitchenItemChangeAdded;
  }

  if (
    normalizedDetail.startsWith("sin ") ||
    normalizedDetail.startsWith("-") ||
    normalizedDetail.startsWith("quitar ")
  ) {
    return styles.kitchenItemChangeRemoved;
  }

  return styles.kitchenItemChangeNeutral;
};

function Cocina() {
  const navigate = useNavigate();
  const {
    canAccessModule,
    canPerformAction,
    currentBranch,
    currentAccessRoleLabel,
    currentRestaurant,
    user,
    isKitchenWorkspace,
    isCreatorMode,
    isSupabaseConfigured,
  } = useAccessControl();
  const [ordersState, setOrdersState] = useState(EMPTY_STATE);
  const [waiterOps, setWaiterOps] = useState(() =>
    getWaiterOperationsState(currentBranch || currentRestaurant)
  );
  const [screenMessage, setScreenMessage] = useState({ type: "", text: "" });
  const [recipeOrderId, setRecipeOrderId] = useState("");
  const [restaurantProfile, setRestaurantProfile] = useState(() =>
    getRestaurantProfile(currentBranch || currentRestaurant)
  );
  const operationalSyncHoldUntilRef = useRef(0);
  const sharedOperationalSyncInFlightRef = useRef(false);
  const recipeHistoryTokenRef = useRef("");
  const activeBranchScope = currentBranch || currentRestaurant;
  const shouldManageStandaloneSharedSync = isKitchenWorkspace;
  const shouldSyncSharedOperationalState = Boolean(
    activeBranchScope?.id && isSupabaseConfigured
  );
  const canSeeKitchenModule = canAccessModule("cocina");
  // Permisos v1.1: cocina solo avanza estados si la capa central habilita modify_orders.
  const canManageKitchenFlow =
    canSeeKitchenModule && canPerformAction("modify_orders");

  const openRecipeModal = useCallback((order) => {
    const orderId = String(order?.id || "").trim();

    if (!orderId) {
      return;
    }

    if (typeof window !== "undefined" && !recipeHistoryTokenRef.current) {
      const token = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const currentState =
        window.history.state && typeof window.history.state === "object"
          ? window.history.state
          : {};

      window.history.pushState(
        {
          ...currentState,
          [KITCHEN_RECIPE_HISTORY_STATE_KEY]: token,
        },
        document.title,
        window.location.href
      );
      recipeHistoryTokenRef.current = token;
    }

    setRecipeOrderId(orderId);
  }, []);

  const closeRecipeModal = useCallback(() => {
    const token = recipeHistoryTokenRef.current;
    recipeHistoryTokenRef.current = "";
    setRecipeOrderId("");

    if (
      typeof window !== "undefined" &&
      token &&
      window.history.state?.[KITCHEN_RECIPE_HISTORY_STATE_KEY] === token
    ) {
      window.history.back();
    }
  }, []);
  const brandTheme = useMemo(
    () => buildRestaurantBrandTheme(restaurantProfile || {}),
    [restaurantProfile]
  );
  const restaurantFaviconUrl =
    brandTheme.effectiveIconLogoUrl ||
    brandTheme.effectiveLogoCompactUrl ||
    "";
  const beginOperationalRemoteSyncHold = useCallback(
    (durationMs = OPERATIONAL_REMOTE_SYNC_HOLD_MS) => {
      operationalSyncHoldUntilRef.current = Math.max(
        operationalSyncHoldUntilRef.current,
        Date.now() + Math.max(0, Number(durationMs || 0))
      );
    },
    []
  );
  const shouldHoldOperationalRemoteSync = useCallback(
    () => Date.now() < operationalSyncHoldUntilRef.current,
    []
  );

  useEffect(() => {
    setRestaurantProfile(getRestaurantProfile(activeBranchScope));

    const handleProfileUpdate = (event) => {
      const updatedRestaurantId = event.detail?.restaurantId || "default";
      const currentRestaurantId = activeBranchScope?.id || "default";

      if (updatedRestaurantId !== currentRestaurantId) {
        return;
      }

      setRestaurantProfile(
        event.detail?.profile || getRestaurantProfile(activeBranchScope)
      );
    };

    const handleStorage = (event) => {
      const expectedKey = buildRestaurantProfileStorageKey(activeBranchScope?.id);

      if (event.key !== expectedKey) {
        return;
      }

      setRestaurantProfile(getRestaurantProfile(activeBranchScope));
    };

    window.addEventListener(RESTAURANT_PROFILE_UPDATED_EVENT, handleProfileUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(RESTAURANT_PROFILE_UPDATED_EVENT, handleProfileUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, [activeBranchScope]);

  useEffect(() => {
    let active = true;

    const syncRemoteProfile = async () => {
      if (!activeBranchScope?.id || isCreatorMode || !isSupabaseConfigured) {
        return;
      }

      try {
        const localProfile = getRestaurantProfile(activeBranchScope);
        const remoteProfile = await getBranchPublicProfile(activeBranchScope.id);
        const mergedProfile = saveRestaurantProfile(
          activeBranchScope,
          mergeRemoteBranchIntoProfilePreservingLocalPreferences(
            localProfile,
            remoteProfile
          )
        );

        if (!active) {
          return;
        }

        setRestaurantProfile(mergedProfile);
      } catch (error) {
        if (!active) {
          return;
        }

        console.error("No pudimos sincronizar la marca remota de la sucursal:", error);
      }
    };

    syncRemoteProfile();

    return () => {
      active = false;
    };
  }, [activeBranchScope, isCreatorMode, isSupabaseConfigured]);

  useEffect(() => {
    if (!shouldManageStandaloneSharedSync) {
      return;
    }

    setDocumentTitle(
      "Cocina",
      currentBranch?.name || activeBranchScope?.name || restaurantProfile?.name || ""
    );
  }, [
    activeBranchScope?.name,
    currentBranch?.name,
    restaurantProfile?.name,
    shouldManageStandaloneSharedSync,
  ]);

  useEffect(() => {
    if (restaurantFaviconUrl) {
      applyHeadBrandingAssets(restaurantFaviconUrl);
      return;
    }

    applyInstitutionalBrandingAssets();
  }, [restaurantFaviconUrl]);

  const syncOrdersState = useCallback(() => {
    if (!activeBranchScope?.id) {
      setOrdersState(EMPTY_STATE);
      return;
    }

    setOrdersState(getOperationalOrdersState(activeBranchScope, () => EMPTY_STATE));
  }, [activeBranchScope]);

  useEffect(() => {
    syncOrdersState();
  }, [syncOrdersState]);

  useEffect(() => {
    operationalSyncHoldUntilRef.current = 0;
  }, [activeBranchScope?.id]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleRecipePopState = (event) => {
      const token = recipeHistoryTokenRef.current;

      if (!token || event.state?.[KITCHEN_RECIPE_HISTORY_STATE_KEY] === token) {
        return;
      }

      recipeHistoryTokenRef.current = "";
      setRecipeOrderId("");
    };

    window.addEventListener("popstate", handleRecipePopState);
    return () => window.removeEventListener("popstate", handleRecipePopState);
  }, []);

  useEffect(() => {
    if (!recipeOrderId || typeof window === "undefined") {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const handleRecipeKeyDown = (event) => {
      if (event.key === "Escape") {
        closeRecipeModal();
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleRecipeKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleRecipeKeyDown);
    };
  }, [closeRecipeModal, recipeOrderId]);

  const syncSharedOperationalState = useCallback(async () => {
    if (!activeBranchScope?.id || !shouldSyncSharedOperationalState) {
      return;
    }

    if (shouldHoldOperationalRemoteSync()) {
      return;
    }

    if (sharedOperationalSyncInFlightRef.current) {
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      SHARED_OPERATIONAL_SYNC_TIMEOUT_MS
    );
    sharedOperationalSyncInFlightRef.current = true;

    try {
      await hydrateSharedOperationalStateToLocal(
        activeBranchScope,
        () => EMPTY_STATE,
        { signal: controller.signal }
      );
    } catch (error) {
      if (error?.name !== "AbortError" && !controller.signal.aborted) {
        console.error(
          "No pudimos sincronizar el estado operativo compartido de Cocina:",
          error
        );
      }
    } finally {
      window.clearTimeout(timeoutId);
      sharedOperationalSyncInFlightRef.current = false;
    }
  }, [
    activeBranchScope,
    shouldHoldOperationalRemoteSync,
    shouldSyncSharedOperationalState,
  ]);

  useEffect(() => {
    if (!activeBranchScope?.id || !shouldSyncSharedOperationalState) {
      return undefined;
    }

    syncSharedOperationalState();

    const intervalId = window.setInterval(
      syncSharedOperationalState,
      SHARED_OPERATIONAL_SYNC_INTERVAL_MS
    );
    const unsubscribeRealtime = subscribeToSharedOperationalStateChanges(
      activeBranchScope.id,
      syncSharedOperationalState
    );
    const handleFocus = () => {
      syncSharedOperationalState();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.clearInterval(intervalId);
      unsubscribeRealtime();
      window.removeEventListener("focus", handleFocus);
    };
  }, [
    activeBranchScope?.id,
    shouldSyncSharedOperationalState,
    syncSharedOperationalState,
  ]);

  useEffect(() => {
    setWaiterOps(getWaiterOperationsState(activeBranchScope));
  }, [activeBranchScope]);

  useEffect(() => {
    if (!activeBranchScope?.id) {
      return undefined;
    }

    const restaurantId = activeBranchScope.id;
    const storageKey = buildWaiterOperationsStorageKey(activeBranchScope.id);

    const syncLocalWaiterOps = () => {
      setWaiterOps(getWaiterOperationsState(activeBranchScope));
    };

    const handleWaiterUpdate = (event) => {
      if ((event.detail?.restaurantId || "default") !== restaurantId) {
        return;
      }

      const nextWaiterOps =
        event.detail?.payload || getWaiterOperationsState(activeBranchScope);

      setWaiterOps(nextWaiterOps);

      if (
        !shouldManageStandaloneSharedSync ||
        event.detail?.source === WAITER_OPERATIONS_REMOTE_SYNC_SOURCE
      ) {
        return;
      }

      void pushSharedWaiterOperationsState(activeBranchScope, nextWaiterOps).catch(
        (error) => {
          console.error(
            "No pudimos sincronizar la operacion compartida de mozos desde Cocina:",
            error
          );
        }
      );
    };

    const handleStorage = (event) => {
      if (event.key && event.key !== storageKey) {
        return;
      }

      syncLocalWaiterOps();
    };

    window.addEventListener(WAITER_OPERATIONS_UPDATED_EVENT, handleWaiterUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        WAITER_OPERATIONS_UPDATED_EVENT,
        handleWaiterUpdate
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, [activeBranchScope, shouldManageStandaloneSharedSync]);

  useEffect(() => {
    if (!activeBranchScope?.id) {
      return undefined;
    }

    const storageKey = buildOperationalOrdersStorageKey(activeBranchScope.id);

    const handleOrdersUpdate = (event) => {
      if (event?.detail?.restaurantId && event.detail.restaurantId !== activeBranchScope.id) {
        return;
      }

      if (shouldHoldOperationalRemoteSync()) {
        return;
      }

      setOrdersState(event?.detail?.payload || getOperationalOrdersState(activeBranchScope, () => EMPTY_STATE));
    };

    const handleStorage = (event) => {
      if (event.key && event.key !== storageKey) {
        return;
      }

      if (shouldHoldOperationalRemoteSync()) {
        return;
      }

      syncOrdersState();
    };

    window.addEventListener(OPERATIONAL_ORDERS_UPDATED_EVENT, handleOrdersUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(OPERATIONAL_ORDERS_UPDATED_EVENT, handleOrdersUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, [activeBranchScope, shouldHoldOperationalRemoteSync, syncOrdersState]);

  const branchKitchenMode = useMemo(
    () => restaurantProfile?.kitchenMode || "manual",
    [restaurantProfile]
  );

  const screenOrders = useMemo(
    () =>
      (ordersState.mesasActivas || [])
        .filter((order) =>
          shouldShowOperationalOrderOnKitchenBoard(order, {
            activeKitchenMode: branchKitchenMode,
          })
        )
        .sort((a, b) => new Date(a.orderedAt || 0) - new Date(b.orderedAt || 0)),
    [branchKitchenMode, ordersState.mesasActivas]
  );

  const recipeOrder = useMemo(
    () =>
      screenOrders.find(
        (order) => String(order?.id || "") === recipeOrderId
      ) || null,
    [recipeOrderId, screenOrders]
  );

  const groupedOrders = useMemo(() => {
    const groups = {
      new: [],
      preparing: [],
      ready: [],
    };

    screenOrders.forEach((order) => {
      const stage = getOperationalOrderKitchenStage(order, {
        activeKitchenMode: branchKitchenMode,
      });
      if (groups[stage]) {
        groups[stage].push(order);
      }
    });

    return groups;
  }, [branchKitchenMode, screenOrders]);

  const summary = useMemo(
    () => ({
      total: screenOrders.length,
      new: groupedOrders.new.length,
      preparing: groupedOrders.preparing.length,
      ready: groupedOrders.ready.length,
    }),
    [groupedOrders, screenOrders.length]
  );

  const persistOrders = useCallback(
    (updater) => {
      if (!activeBranchScope?.id) {
        return null;
      }

      beginOperationalRemoteSyncHold();
      const currentState = getOperationalOrdersState(activeBranchScope, () => EMPTY_STATE);
      const nextState = typeof updater === "function" ? updater(currentState) : updater;
      const savedState = saveOperationalOrdersState(activeBranchScope, nextState, () => EMPTY_STATE);
      void pushSharedOperationalState(activeBranchScope, savedState, () => EMPTY_STATE).catch(
        (error) => {
          console.error(
            "No pudimos sincronizar el estado operativo compartido de Cocina:",
            error
          );
        }
      );
      setOrdersState(savedState);
      return savedState;
    },
    [activeBranchScope, beginOperationalRemoteSyncHold]
  );

  const notifyKitchenPermissionDenied = useCallback(
    (actionLabel) => {
      setScreenMessage({
        type: "error",
        text: `${currentAccessRoleLabel} no puede ${actionLabel} desde Cocina.`,
      });
    },
    [currentAccessRoleLabel]
  );

  const markOrderTakenByKitchen = useCallback(
    (order) => {
      if (!canManageKitchenFlow) {
        notifyKitchenPermissionDenied("tomar pedidos");
        return;
      }

      persistOrders((currentState) => ({
        ...currentState,
        mesasActivas: (currentState.mesasActivas || []).map((item) =>
          item.id !== order.id
            ? item
            : {
                ...item,
                phase: "preparation",
                kitchenDispatchStatus: "screen_confirmed",
                kitchenDispatchedAt: item.kitchenDispatchedAt || new Date().toISOString(),
                kitchenDispatchedBy: item.kitchenDispatchedBy || user?.id || "",
                stockDiscounted: item.stockDiscounted === true,
              }
        ),
      }));

      setScreenMessage({
        type: "success",
        text: `${order.mesa} ya quedó tomada en pantalla de cocina.`,
      });
    },
    [canManageKitchenFlow, notifyKitchenPermissionDenied, persistOrders, user?.id]
  );

  const markOrderReadyFromKitchen = useCallback(
    (order) => {
      if (!canManageKitchenFlow) {
        notifyKitchenPermissionDenied("marcar pedidos como listos");
        return;
      }

      const isExternalOrder = Boolean(order.externalOrderId);
      const assignment = order.tableId
        ? waiterOps.tableAssignments.find(
            (waiterAssignment) => waiterAssignment.tableId === order.tableId
          ) || null
        : null;
      const broadcast = !assignment?.waiterUserId;

      if (!isExternalOrder) {
        createDeliveryAlert(activeBranchScope, {
          id: `delivery-ready-${order.id}`,
          orderId: order.id,
          tableId: order.tableId || "",
          mesa: order.mesa,
          source: order.source || "",
          requestedAt: new Date().toISOString(),
          broadcast,
          targetWaiterIds: broadcast ? [] : [assignment.waiterUserId],
          targetWaiterNames: broadcast || !assignment?.waiterName ? [] : [assignment.waiterName],
        });
      }

      const nextKitchenDispatchStatus = isKioskPrinterKitchenOrder(order)
        ? "printer_ready"
        : "screen_ready";

      persistOrders((currentState) => ({
        ...currentState,
        mesasActivas: (currentState.mesasActivas || []).map((item) =>
          item.id !== order.id
            ? item
            : {
                ...item,
                phase: "ready",
                kitchenDispatchStatus: nextKitchenDispatchStatus,
                assignedWaiterUserId: assignment?.waiterUserId || item.assignedWaiterUserId || "",
                assignedWaiterName: assignment?.waiterName || item.assignedWaiterName || "",
              }
        ),
      }));

      if (isExternalOrder) {
        const nextExternalStatus =
          String(order.canal || "").trim().toLowerCase() === "delivery"
            ? "ready_for_delivery"
            : "ready_for_pickup";
        void transitionExternalOrderStatus({
          orderId: order.externalOrderId,
          nextStatus: nextExternalStatus,
          note: "Cocina marcó el pedido como listo.",
        }).catch((statusError) => {
          setScreenMessage({
            type: "error",
            text:
              statusError?.message ||
              "La comanda quedó lista, pero no pudimos actualizar Delivery.",
          });
        });
      }

      setScreenMessage({
        type: "success",
        text: isExternalOrder
          ? `${order.mesa} quedó listo para coordinar su entrega.`
          : broadcast
          ? `${order.mesa} quedó lista y ya se avisó al equipo de salón.`
          : `${order.mesa} quedó lista y ya se avisó a ${assignment.waiterName}.`,
      });
    },
    [
      activeBranchScope,
      canManageKitchenFlow,
      notifyKitchenPermissionDenied,
      persistOrders,
      waiterOps.tableAssignments,
    ]
  );

  const returnOrderToKitchen = useCallback(
    (order) => {
      if (!canManageKitchenFlow) {
        notifyKitchenPermissionDenied("devolver pedidos a cocina");
        return;
      }

      const nextKitchenDispatchStatus = isKioskPrinterKitchenOrder(order)
        ? "printer_printed"
        : "screen_confirmed";

      persistOrders((currentState) => ({
        ...currentState,
        mesasActivas: (currentState.mesasActivas || []).map((item) =>
          item.id !== order.id
            ? item
            : {
                ...item,
                phase: "preparation",
                kitchenDispatchStatus: nextKitchenDispatchStatus,
              }
        ),
      }));

      setScreenMessage({
        type: "info",
        text: `${order.mesa} volvió a cocina para seguir preparándose.`,
      });
    },
    [canManageKitchenFlow, notifyKitchenPermissionDenied, persistOrders]
  );

  useEffect(() => {
    if (!screenMessage.text) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setScreenMessage({ type: "", text: "" });
    }, 4200);

    return () => window.clearTimeout(timer);
  }, [screenMessage]);

  const renderOrderCard = (order, stage) => {
    const customerName = getCustomerName(order);
    const assignment = order.tableId
      ? waiterOps.tableAssignments.find(
          (waiterAssignment) => waiterAssignment.tableId === order.tableId
        ) || null
      : null;

    return (
      <article key={order.id} style={styles.orderCard}>
        <div style={styles.orderHeader}>
          <div style={styles.orderHeaderText}>
            <strong style={styles.orderTitle}>{order.mesa}</strong>
            <div style={styles.orderMeta}>
              {order.comensales || 1} comensales · {order.canal || "Salon"}
              {customerName ? ` · ${customerName}` : ""}
            </div>
          </div>
          <span
            style={{
              ...styles.stageBadge,
              ...(stage === "new"
                ? styles.stageBadgeNew
                : stage === "preparing"
                  ? styles.stageBadgePreparing
                  : styles.stageBadgeReady),
            }}
          >
            {stage === "new" ? "Nuevo en cocina" : stage === "preparing" ? "En cocina" : "Listo"}
          </span>
        </div>

        <div style={styles.cardInfoGrid}>
          <div style={styles.infoCard}>
            <span style={styles.infoLabel}>Pedido</span>
            <div style={styles.infoText}>{getOrderSummary(order)}</div>
          </div>
          <div style={styles.infoCard}>
            <span style={styles.infoLabel}>Items</span>
            <div style={styles.infoStrong}>{getOrderItemCount(order)}</div>
          </div>
          <div style={styles.infoCard}>
            <span style={styles.infoLabel}>Total</span>
            <div style={styles.infoStrong}>{formatCurrency(order.totalAmount)}</div>
          </div>
          <div style={styles.infoCard}>
            <span style={styles.infoLabel}>Tiempo</span>
            <div style={styles.infoStrong}>{formatElapsed(order.orderedAt)}</div>
          </div>
          <div style={styles.infoCard}>
            <span style={styles.infoLabel}>Estado en cocina</span>
            <div style={styles.infoStrong}>{getScreenKitchenStageLabel(stage)}</div>
            <div style={styles.infoText}>{getScreenKitchenStageDetail(stage)}</div>
          </div>
          <div style={styles.infoCard}>
            <span style={styles.infoLabel}>Siguiente paso</span>
            <div style={styles.infoText}>{getScreenKitchenNextStep(stage)}</div>
          </div>
        </div>

        <div style={styles.kitchenItemList}>
          {filterKitchenOrderItems(order?.items).map((item, itemIndex) => {
            const selectionDetails = getOrderItemSelectionDetails(item);
            return (
              <div key={item.id || `${order.id}-item-${itemIndex}`} style={styles.kitchenItemRow}>
                <div style={styles.kitchenItemTitle}>
                  <strong>{Number(item.quantity || 1)}x {item.nombre || item.productName || "Producto"}</strong>
                </div>
                {selectionDetails.length ? (
                  <div style={styles.kitchenItemChanges}>
                    {selectionDetails.map((detail, detailIndex) => (
                      <React.Fragment key={`${item.id || itemIndex}-change-${detailIndex}`}>
                        {detailIndex > 0 ? (
                          <span style={styles.kitchenItemChangeSeparator}> | </span>
                        ) : null}
                        <span style={getSelectionDetailStyle(detail)}>{detail}</span>
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <div style={styles.kitchenItemNormal}>Preparacion normal</div>
                )}
                {item.notes ? <div style={styles.kitchenItemNotes}>Nota: {item.notes}</div> : null}
              </div>
            );
          })}
        </div>

        {assignment?.waiterName ? (
          <div style={styles.assignmentNotice}>Mozo asignado: {assignment.waiterName}</div>
        ) : null}

        <div style={styles.cardActions}>
          <button
            type="button"
            style={styles.recipeButton}
            onClick={() => openRecipeModal(order)}
          >
            Receta
          </button>
          {stage === "new" ? (
            <button
              type="button"
              style={styles.primaryButton}
              onClick={() => markOrderTakenByKitchen(order)}
              disabled={!canManageKitchenFlow}
            >
              Tomar en cocina
            </button>
          ) : null}
          {stage === "preparing" ? (
            <button
              type="button"
              style={styles.primaryButton}
              onClick={() => markOrderReadyFromKitchen(order)}
              disabled={!canManageKitchenFlow}
            >
              Marcar listo
            </button>
          ) : null}
          {stage === "ready" ? (
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => returnOrderToKitchen(order)}
              disabled={!canManageKitchenFlow}
            >
              Volver a cocina
            </button>
          ) : null}
          {!isKitchenWorkspace ? (
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => navigate("/app")}
            >
              Ver en Inicio
            </button>
          ) : null}
        </div>
      </article>
    );
  };

  const modeNotice =
    branchKitchenMode !== "screen"
      ? branchKitchenMode === "printer"
        ? restaurantProfile?.serviceMode === "kiosk"
          ? "En kiosk con impresora, este tablero tambien muestra los pedidos cuya comanda ya fue impresa para que cocina pueda marcarlos listos y el cliente vea su numero."
          : "La sucursal usa comandas impresas. Este tablero queda disponible para los pedidos compatibles que necesiten marcarse como listos desde cocina."
        : "La sucursal activa no esta configurada en modo pantalla. Igual dejamos visible este tablero para cerrar pedidos que ya nacieron con ese flujo."
      : "Los pedidos pasan aca cuando desde Inicio se los manda a preparacion en una sucursal configurada con pantalla de cocina.";

  return (
    <div style={styles.page}>
      <section style={styles.hero}>
        <div>
          <span style={styles.eyebrow}>Operacion de cocina</span>
          <h1 style={styles.title}>Pantalla de cocina</h1>
          <p style={styles.subtitle}>{modeNotice}</p>
        </div>
        <div style={styles.heroBadge}>
          <span style={styles.heroBadgeLabel}>Modo actual</span>
          <strong style={styles.heroBadgeValue}>
            {branchKitchenMode === "screen"
              ? "Pantalla"
              : branchKitchenMode === "printer"
                ? "Impresora"
                : "Manual"}
          </strong>
        </div>
      </section>

      {screenMessage.text ? (
        <section
          style={{
            ...styles.toast,
            ...(screenMessage.type === "success"
              ? styles.toastSuccess
              : screenMessage.type === "info"
                ? styles.toastInfo
                : {}),
          }}
        >
          {screenMessage.text}
        </section>
      ) : null}

      <section style={styles.summaryGrid}>
        <article style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Pedidos visibles</span>
          <strong style={styles.summaryValue}>{summary.total}</strong>
          <span style={styles.summaryHint}>Pantalla y kiosk impreso</span>
        </article>
        <article style={{ ...styles.summaryCard, ...styles.summaryCardNew }}>
          <span style={styles.summaryLabel}>Nuevos</span>
          <strong style={styles.summaryValue}>{summary.new}</strong>
          <span style={styles.summaryHint}>Pendientes de tomar en cocina</span>
        </article>
        <article style={{ ...styles.summaryCard, ...styles.summaryCardPreparing }}>
          <span style={styles.summaryLabel}>En cocina</span>
          <strong style={styles.summaryValue}>{summary.preparing}</strong>
          <span style={styles.summaryHint}>Tomados o impresos para cocina</span>
        </article>
        <article style={{ ...styles.summaryCard, ...styles.summaryCardReady }}>
          <span style={styles.summaryLabel}>Listos</span>
          <strong style={styles.summaryValue}>{summary.ready}</strong>
          <span style={styles.summaryHint}>Esperando entrega desde salón</span>
        </article>
      </section>

      <section style={styles.board}>
        <div style={styles.column}>
          <div style={styles.columnHeader}>
            <h2 style={styles.columnTitle}>Nuevos</h2>
            <span style={styles.columnCount}>{summary.new}</span>
          </div>
          <div style={styles.columnBody}>
            {groupedOrders.new.length > 0 ? (
              groupedOrders.new.map((order) => renderOrderCard(order, "new"))
            ) : (
              <div style={styles.emptyCard}>No hay pedidos nuevos en pantalla.</div>
            )}
          </div>
        </div>

        <div style={styles.column}>
          <div style={styles.columnHeader}>
            <h2 style={styles.columnTitle}>En preparación</h2>
            <span style={styles.columnCount}>{summary.preparing}</span>
          </div>
          <div style={styles.columnBody}>
            {groupedOrders.preparing.length > 0 ? (
              groupedOrders.preparing.map((order) => renderOrderCard(order, "preparing"))
            ) : (
              <div style={styles.emptyCard}>Nada en cocina ahora mismo.</div>
            )}
          </div>
        </div>

        <div style={styles.column}>
          <div style={styles.columnHeader}>
            <h2 style={styles.columnTitle}>Listos</h2>
            <span style={styles.columnCount}>{summary.ready}</span>
          </div>
          <div style={styles.columnBody}>
            {groupedOrders.ready.length > 0 ? (
              groupedOrders.ready.map((order) => renderOrderCard(order, "ready"))
            ) : (
              <div style={styles.emptyCard}>Todavía no hay pedidos listos para entregar.</div>
            )}
          </div>
        </div>
      </section>

      {recipeOrder ? (
        <div style={styles.recipeOverlay} onClick={closeRecipeModal}>
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="kitchen-recipe-title"
            style={styles.recipeModal}
            onClick={(event) => event.stopPropagation()}
          >
            <header style={styles.recipeHeader}>
              <div style={styles.recipeHeaderText}>
                <span style={styles.recipeEyebrow}>Comanda de cocina</span>
                <h2 id="kitchen-recipe-title" style={styles.recipeTitle}>
                  Receta de {recipeOrder.mesa || "este pedido"}
                </h2>
                <span style={styles.recipeMeta}>
                  {getCustomerName(recipeOrder)
                    ? `Pedido de ${getCustomerName(recipeOrder)}`
                    : `${recipeOrder.comensales || 1} comensales`}
                </span>
              </div>
              <button
                type="button"
                style={styles.recipeCloseButton}
                onClick={closeRecipeModal}
                aria-label="Cerrar receta"
                title="Cerrar receta"
              >
                X
              </button>
            </header>

            <div style={styles.recipeBody}>
              {filterKitchenOrderItems(recipeOrder.items).map((item, itemIndex) => {
                const ingredients = getKitchenRecipeIngredients(item);
                const selectionDetails = getOrderItemSelectionDetails(item);
                const itemQuantity = Math.max(1, Number(item.quantity || 1));

                return (
                  <section
                    key={item.id || `${recipeOrder.id}-recipe-${itemIndex}`}
                    style={styles.recipeItemSection}
                  >
                    <div style={styles.recipeItemHeader}>
                      <strong style={styles.recipeItemTitle}>
                        {itemQuantity}x {item.productName || item.name || item.nombre || "Producto"}
                      </strong>
                      {itemQuantity > 1 ? (
                        <span style={styles.recipeItemQuantity}>Cantidades totales</span>
                      ) : null}
                    </div>

                    {selectionDetails.length ? (
                      <div style={styles.recipeChanges}>
                        <span style={styles.recipeSectionLabel}>Cambios pedidos</span>
                        <div style={styles.recipeChangeList}>
                          {selectionDetails.map((detail, detailIndex) => (
                            <span
                              key={`${item.id || itemIndex}-recipe-change-${detailIndex}`}
                              style={getSelectionDetailStyle(detail)}
                            >
                              {detail}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    <span style={styles.recipeSectionLabel}>Ingredientes</span>
                    {ingredients.length ? (
                      <ul style={styles.recipeIngredientList}>
                        {ingredients.map((ingredient) => (
                          <li
                            key={`${ingredient.id}-${ingredient.unit}`}
                            style={styles.recipeIngredientRow}
                          >
                            <span style={styles.recipeIngredientName}>{ingredient.name}</span>
                            <strong style={styles.recipeIngredientQuantity}>
                              {formatKitchenRecipeQuantity(ingredient.quantity)} {ingredient.unit}
                            </strong>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div style={styles.recipeEmpty}>
                        Este producto no tiene ingredientes de receta cargados.
                      </div>
                    )}

                    {item.notes ? (
                      <div style={styles.recipeNotes}>Nota: {item.notes}</div>
                    ) : null}
                  </section>
                );
              })}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  page: {
    display: "grid",
    gap: "18px",
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    padding: "22px 24px",
    borderRadius: "24px",
    border: "1px solid var(--brand-border, #dbe2ea)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)",
    boxShadow: "0 18px 36px rgba(15, 23, 42, 0.06)",
  },
  eyebrow: {
    display: "inline-flex",
    marginBottom: "10px",
    padding: "6px 10px",
    borderRadius: "999px",
    backgroundColor: "var(--brand-soft, #eff6ff)",
    color: "var(--brand-primary, #2563eb)",
    fontSize: "12px",
    fontWeight: 800,
    textTransform: "uppercase",
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: "32px",
    lineHeight: 1.05,
  },
  subtitle: {
    marginTop: "10px",
    marginBottom: 0,
    maxWidth: "780px",
    color: "#475569",
    lineHeight: 1.6,
  },
  heroBadge: {
    minWidth: "180px",
    display: "grid",
    gap: "6px",
    padding: "16px 18px",
    borderRadius: "18px",
    border: "1px solid var(--brand-border, #dbe2ea)",
    backgroundColor: "#ffffff",
  },
  heroBadgeLabel: {
    color: "#64748b",
    fontSize: "12px",
    textTransform: "uppercase",
    fontWeight: 800,
  },
  heroBadgeValue: {
    color: "var(--brand-primary, #2563eb)",
    fontSize: "22px",
  },
  toast: {
    padding: "14px 16px",
    borderRadius: "16px",
    border: "1px solid #dbeafe",
    backgroundColor: "#f8fbff",
    color: "#1d4ed8",
    fontWeight: 700,
  },
  toastSuccess: {
    border: "1px solid #bbf7d0",
    backgroundColor: "#f0fdf4",
    color: "#166534",
  },
  toastInfo: {
    border: "1px solid #bfdbfe",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px",
  },
  summaryCard: {
    display: "grid",
    gap: "6px",
    padding: "18px",
    borderRadius: "20px",
    border: "1px solid var(--brand-border, #dbe2ea)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)",
    boxShadow: "0 16px 30px rgba(15, 23, 42, 0.05)",
  },
  summaryCardNew: {
    border: "1px solid #fdba74",
    background:
      "linear-gradient(180deg, rgba(255,251,235,1) 0%, rgba(255,247,237,0.98) 100%)",
  },
  summaryCardPreparing: {
    border: "1px solid #93c5fd",
    background:
      "linear-gradient(180deg, rgba(248,251,255,1) 0%, rgba(239,246,255,0.98) 100%)",
  },
  summaryCardReady: {
    border: "1px solid #86efac",
    background:
      "linear-gradient(180deg, rgba(248,255,250,1) 0%, rgba(240,253,244,0.98) 100%)",
  },
  summaryLabel: {
    color: "#64748b",
    fontSize: "12px",
    textTransform: "uppercase",
    fontWeight: 800,
  },
  summaryValue: {
    color: "#0f172a",
    fontSize: "30px",
    lineHeight: 1,
  },
  summaryHint: {
    color: "#475569",
    fontSize: "13px",
    lineHeight: 1.45,
  },
  board: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "16px",
    alignItems: "start",
  },
  column: {
    minWidth: 0,
    display: "grid",
    gap: "12px",
  },
  columnHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
  },
  columnTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "22px",
  },
  columnCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "34px",
    height: "34px",
    padding: "0 10px",
    borderRadius: "999px",
    backgroundColor: "var(--brand-soft, #eff6ff)",
    color: "var(--brand-primary, #2563eb)",
    fontWeight: 800,
  },
  columnBody: {
    display: "grid",
    gap: "12px",
  },
  orderCard: {
    display: "grid",
    gap: "14px",
    padding: "18px",
    borderRadius: "20px",
    border: "1px solid var(--brand-border, #dbe2ea)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)",
    boxShadow: "0 16px 30px rgba(15, 23, 42, 0.05)",
  },
  orderHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "10px",
  },
  orderHeaderText: {
    display: "grid",
    gap: "6px",
  },
  orderTitle: {
    color: "#0f172a",
    fontSize: "20px",
  },
  orderMeta: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.45,
  },
  stageBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "30px",
    padding: "0 10px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 800,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  stageBadgeNew: {
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    border: "1px solid #fdba74",
  },
  stageBadgePreparing: {
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
    border: "1px solid #93c5fd",
  },
  stageBadgeReady: {
    backgroundColor: "#dcfce7",
    color: "#166534",
    border: "1px solid #86efac",
  },
  cardInfoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "10px",
  },
  infoCard: {
    display: "grid",
    gap: "6px",
    padding: "12px 14px",
    borderRadius: "14px",
    backgroundColor: "rgba(255,255,255,0.82)",
    border: "1px solid rgba(226, 232, 240, 0.95)",
  },
  infoLabel: {
    color: "#64748b",
    fontSize: "11px",
    textTransform: "uppercase",
    fontWeight: 800,
  },
  infoText: {
    color: "#1e293b",
    fontSize: "14px",
    lineHeight: 1.5,
    fontWeight: 700,
  },
  infoStrong: {
    color: "#0f172a",
    fontSize: "16px",
    lineHeight: 1.35,
    fontWeight: 800,
  },
  kitchenItemList: {
    display: "grid",
    gap: "9px",
    padding: "12px",
    borderRadius: "16px",
    backgroundColor: "#fff",
    border: "1px solid #dbe5f0",
  },
  kitchenItemRow: {
    display: "grid",
    gap: "4px",
    padding: "10px 11px",
    borderRadius: "12px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
  },
  kitchenItemTitle: {
    color: "#0f172a",
    fontSize: "15px",
  },
  kitchenItemChanges: {
    fontSize: "14px",
    lineHeight: 1.45,
    fontWeight: 900,
  },
  kitchenItemChangeAdded: {
    color: "#15803d",
  },
  kitchenItemChangeRemoved: {
    color: "#b42318",
  },
  kitchenItemChangeNeutral: {
    color: "#334155",
  },
  kitchenItemChangeSeparator: {
    color: "#94a3b8",
  },
  kitchenItemNormal: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 700,
  },
  kitchenItemNotes: {
    color: "#7c2d12",
    fontSize: "13px",
    lineHeight: 1.4,
    fontWeight: 800,
  },
  assignmentNotice: {
    padding: "12px 14px",
    borderRadius: "14px",
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    fontWeight: 700,
  },
  cardActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap",
  },
  recipeButton: {
    border: "1px solid #fdba74",
    borderRadius: "12px",
    padding: "11px 14px",
    backgroundColor: "#fff7ed",
    color: "#9a3412",
    fontWeight: 800,
    cursor: "pointer",
  },
  primaryButton: {
    border: "none",
    borderRadius: "12px",
    padding: "11px 14px",
    background: "var(--brand-hero, linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%))",
    color: "var(--brand-secondary, #ffffff)",
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 14px 24px rgba(15, 23, 42, 0.18)",
  },
  secondaryButton: {
    border: "1px solid #dbe2ea",
    borderRadius: "12px",
    padding: "11px 14px",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontWeight: 800,
    cursor: "pointer",
  },
  recipeOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1400,
    display: "grid",
    placeItems: "center",
    padding: "16px",
    backgroundColor: "rgba(15, 23, 42, 0.64)",
  },
  recipeModal: {
    width: "min(720px, calc(100vw - 32px))",
    maxHeight: "min(820px, calc(100vh - 32px))",
    display: "grid",
    gridTemplateRows: "auto minmax(0, 1fr)",
    overflow: "hidden",
    borderRadius: "18px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    boxShadow: "0 28px 80px rgba(15, 23, 42, 0.28)",
  },
  recipeHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    padding: "20px 22px",
    borderBottom: "1px solid #e2e8f0",
  },
  recipeHeaderText: {
    minWidth: 0,
    display: "grid",
    gap: "5px",
  },
  recipeEyebrow: {
    color: "#c2410c",
    fontSize: "11px",
    fontWeight: 900,
    textTransform: "uppercase",
  },
  recipeTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "24px",
    lineHeight: 1.2,
  },
  recipeMeta: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 700,
  },
  recipeCloseButton: {
    flex: "0 0 auto",
    width: "40px",
    height: "40px",
    display: "inline-grid",
    placeItems: "center",
    borderRadius: "50%",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontSize: "18px",
    fontWeight: 900,
    cursor: "pointer",
  },
  recipeBody: {
    minHeight: 0,
    overflowY: "auto",
    padding: "0 22px 22px",
  },
  recipeItemSection: {
    display: "grid",
    gap: "12px",
    padding: "20px 0",
    borderBottom: "1px solid #e2e8f0",
  },
  recipeItemHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "12px",
  },
  recipeItemTitle: {
    color: "#0f172a",
    fontSize: "18px",
    lineHeight: 1.35,
  },
  recipeItemQuantity: {
    flex: "0 0 auto",
    color: "#64748b",
    fontSize: "11px",
    fontWeight: 800,
    textTransform: "uppercase",
  },
  recipeSectionLabel: {
    color: "#64748b",
    fontSize: "11px",
    fontWeight: 900,
    textTransform: "uppercase",
  },
  recipeChanges: {
    display: "grid",
    gap: "7px",
    padding: "10px 12px",
    borderLeft: "3px solid #fb923c",
    backgroundColor: "#fffaf5",
  },
  recipeChangeList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px 14px",
    fontSize: "14px",
    fontWeight: 900,
  },
  recipeIngredientList: {
    display: "grid",
    gap: 0,
    margin: 0,
    padding: 0,
    listStyle: "none",
    borderTop: "1px solid #e2e8f0",
  },
  recipeIngredientRow: {
    minHeight: "42px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "9px 2px",
    borderBottom: "1px solid #e2e8f0",
  },
  recipeIngredientName: {
    minWidth: 0,
    color: "#1e293b",
    fontSize: "15px",
    fontWeight: 750,
    overflowWrap: "anywhere",
  },
  recipeIngredientQuantity: {
    flex: "0 0 auto",
    color: "#0f172a",
    fontSize: "15px",
    whiteSpace: "nowrap",
  },
  recipeEmpty: {
    padding: "12px 0",
    color: "#64748b",
    fontSize: "14px",
    lineHeight: 1.5,
  },
  recipeNotes: {
    padding: "10px 12px",
    borderLeft: "3px solid #f59e0b",
    backgroundColor: "#fffbeb",
    color: "#7c2d12",
    fontSize: "14px",
    lineHeight: 1.45,
    fontWeight: 800,
  },
  emptyCard: {
    padding: "22px 18px",
    borderRadius: "18px",
    border: "1px dashed #cbd5e1",
    backgroundColor: "rgba(255,255,255,0.72)",
    color: "#64748b",
    lineHeight: 1.6,
    textAlign: "center",
  },
};

export default Cocina;
